import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import Logo from './Logo';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [articlesDropdownOpen, setArticlesDropdownOpen] = useState(false);
  const [omSkatDropdownOpen, setOmSkatDropdownOpen] = useState(false);
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setArticlesDropdownOpen(false);
    setOmSkatDropdownOpen(false);
  }, [location.pathname, location.search]);

  // Helper to check if a dropdown sub-link is active
  const isDropdownActive = (path, searchParam = '') => {
    if (searchParam) {
      return location.pathname === path && location.search.includes(searchParam);
    }
    return location.pathname === path && (!location.search || location.search === '');
  };

  return (
    <header className="w-full bg-white text-[#010b27] border-b border-slate-200">
      {/* GRAND NEWSPAPER MASTHEAD */}
      <div className="mx-auto max-w-7xl px-4 py-5 sm:py-7 bg-white">
        <div className="relative flex flex-col items-center justify-center">
          {/* Mobile hamburger toggle: visible only below 1200px */}
          <div className="min-[1200px]:hidden absolute left-0 top-1">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center rounded-sm border border-slate-200 p-2 text-[#010b27] hover:bg-[#010b27] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#fd7302]"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn avisens menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} aria-hidden="true" />
            </button>
          </div>

          {/* Centered Masthead Logo with bigger section buttons directly underneath */}
          <div className="flex flex-col items-center justify-center text-center">
            <Logo variant="masthead" />

            {/* Audience buttons right under logo: bigger format */}
            <div className="mt-3.5 sm:mt-4 inline-flex items-center rounded-md border border-slate-200 p-1 bg-slate-50/80 shadow-xs">
              <NavLink
                to="/private"
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all rounded-sm ${
                    isActive
                      ? 'bg-[#010b27] text-white shadow-xs'
                      : 'text-[#010b27] hover:text-[#fd7302] hover:bg-white'
                  }`
                }
              >
                <i className="fa-solid fa-user text-xs sm:text-sm" aria-hidden="true" />
                <span>Private</span>
              </NavLink>
              <NavLink
                to="/erhvervsdrivende"
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all rounded-sm ${
                    isActive
                      ? 'bg-[#fd7302] text-white shadow-xs'
                      : 'text-[#010b27] hover:text-[#fd7302] hover:bg-white'
                  }`
                }
              >
                <i className="fa-solid fa-building text-xs sm:text-sm" aria-hidden="true" />
                <span>Erhvervsdrivende</span>
              </NavLink>
            </div>
          </div>
        </div>
      </div>

      {/* 4. MAIN NAVIGATION (Desktop: strictly >= 1200px, Mobile: burger drawer) */}
      <nav
        aria-label="Hovednavigation"
        className="hidden min-[1200px]:block bg-white border-b border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-4">
          <ul className="flex items-center justify-between text-[13px] font-semibold tracking-wide uppercase">
            {/* Forside */}
            <li>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `inline-block py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                Forside
              </NavLink>
            </li>

            {/* Private */}
            <li>
              <NavLink
                to="/private"
                className={({ isActive }) =>
                  `inline-block py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#010b27] text-[#010b27] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                Private
              </NavLink>
            </li>

            {/* Erhvervsdrivende */}
            <li>
              <NavLink
                to="/erhvervsdrivende"
                className={({ isActive }) =>
                  `inline-block py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                Erhvervsdrivende
              </NavLink>
            </li>

            {/* Artikler with dropdown */}
            <li
              className="relative"
              onMouseEnter={() => setArticlesDropdownOpen(true)}
              onMouseLeave={() => setArticlesDropdownOpen(false)}
            >
              <NavLink
                to="/artikler"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                <span>Artikler</span>
                <i className={`fa-solid fa-chevron-down text-[9px] transition-transform ${articlesDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </NavLink>

              {articlesDropdownOpen && (
                <div className="absolute left-0 top-full z-40 w-64 border border-slate-200 bg-white py-2 shadow-lg rounded-md overflow-hidden">
                  <div className="px-4 py-1.5 text-[10px] font-bold tracking-widest text-slate-400 uppercase border-b border-slate-100">
                    Faglige kategorier
                  </div>
                  <Link
                    to="/artikler"
                    onClick={() => setArticlesDropdownOpen(false)}
                    className={`block px-4 py-2 text-xs transition-colors ${
                      isDropdownActive('/artikler')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'font-semibold text-[#010b27] hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Alle artikler
                  </Link>
                  <Link
                    to="/artikler?kategori=moms-afgifter"
                    onClick={() => setArticlesDropdownOpen(false)}
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      isDropdownActive('/artikler', 'kategori=moms-afgifter')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Moms & Afgifter
                  </Link>
                  <Link
                    to="/artikler?kategori=indkomstskat"
                    onClick={() => setArticlesDropdownOpen(false)}
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      isDropdownActive('/artikler', 'kategori=indkomstskat')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Indkomstskat & Fradrag
                  </Link>
                  <Link
                    to="/artikler?kategori=selskabsskat"
                    onClick={() => setArticlesDropdownOpen(false)}
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      isDropdownActive('/artikler', 'kategori=selskabsskat')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Selskabsskat & Erhverv
                  </Link>
                  <Link
                    to="/artikler?kategori=skattekontrol"
                    onClick={() => setArticlesDropdownOpen(false)}
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      isDropdownActive('/artikler', 'kategori=skattekontrol')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Skattekontrol & Tilsyn
                  </Link>
                  <Link
                    to="/artikler?kategori=ejendomsskat"
                    onClick={() => setArticlesDropdownOpen(false)}
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      isDropdownActive('/artikler', 'kategori=ejendomsskat')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Bolig & Ejendomsskat
                  </Link>
                </div>
              )}
            </li>

            {/* Presse & Film */}
            <li>
              <NavLink
                to="/presse-og-film"
                className={({ isActive }) =>
                  `inline-block py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                Presse & Film
              </NavLink>
            </li>

            {/* Om skat with dropdown */}
            <li
              className="relative"
              onMouseEnter={() => setOmSkatDropdownOpen(true)}
              onMouseLeave={() => setOmSkatDropdownOpen(false)}
            >
              <NavLink
                to="/om-skat"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                <span>Om skat</span>
                <i className={`fa-solid fa-chevron-down text-[9px] transition-transform ${omSkatDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </NavLink>

              {omSkatDropdownOpen && (
                <div className="absolute left-0 top-full z-40 w-72 border border-slate-200 bg-white py-2 shadow-lg rounded-md overflow-hidden">
                  <div className="px-4 py-1.5 text-[10px] font-bold tracking-widest text-slate-400 uppercase border-b border-slate-100">
                    Vidensunivers efter emner
                  </div>
                  <Link
                    to="/om-skat"
                    className={`block px-4 py-2 text-xs transition-colors ${
                      isDropdownActive('/om-skat')
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'font-semibold text-[#010b27] hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Oversigt over vidensunivers
                  </Link>
                  <Link
                    to="/om-skat/indkomstskat-og-fradragsret"
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      location.pathname === '/om-skat/indkomstskat-og-fradragsret'
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Indkomstskat & Fradragsret
                  </Link>
                  <Link
                    to="/om-skat/moms-og-afregning-smv"
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      location.pathname === '/om-skat/moms-og-afregning-smv'
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Moms & Momsafregning for SMV
                  </Link>
                  <Link
                    to="/om-skat/skattekontrol-og-tilsyn"
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      location.pathname === '/om-skat/skattekontrol-og-tilsyn'
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Skattekontrol & Rettigheder
                  </Link>
                  <Link
                    to="/om-skat/boligskat-og-ejendomsvurdering"
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      location.pathname === '/om-skat/boligskat-og-ejendomsvurdering'
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Boligskat & Ejendomsvurderinger
                  </Link>
                  <Link
                    to="/om-skat/selskabsskat-og-udbytte"
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      location.pathname === '/om-skat/selskabsskat-og-udbytte'
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Selskabsskat & Udbytte
                  </Link>
                  <Link
                    to="/om-skat/klageprocessen-og-skatteankestyrelsen"
                    className={`block px-4 py-2 text-xs normal-case transition-colors ${
                      location.pathname === '/om-skat/klageprocessen-og-skatteankestyrelsen'
                        ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-4 border-[#fd7302]'
                        : 'text-slate-600 hover:bg-[#fd7302] hover:text-white'
                    }`}
                  >
                    Klageprocessen & Klageinstanser
                  </Link>
                </div>
              )}
            </li>

            {/* Har brug for hjælp */}
            <li>
              <NavLink
                to="/kontakt"
                className={({ isActive }) =>
                  `inline-block py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#fd7302] hover:text-[#e06500] hover:border-[#fd7302]/50 font-bold'
                  }`
                }
              >
                Har brug for hjælp
              </NavLink>
            </li>

            {/* Kontakt */}
            <li>
              <NavLink
                to="/kontakt"
                className={({ isActive }) =>
                  `inline-block py-3 px-3 transition-colors border-b-2 ${
                    isActive
                      ? 'border-[#fd7302] text-[#fd7302] font-bold'
                      : 'border-transparent text-[#010b27] hover:text-[#fd7302] hover:border-[#fd7302]/50'
                  }`
                }
              >
                Kontakt
              </NavLink>
            </li>
          </ul>
        </div>
      </nav>

      {/* 5. MOBILE DRAWER MENU (Under 1200px only) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-200 bg-white px-4 py-6 shadow-xl">
          <div className="flex flex-col gap-1 text-sm font-semibold uppercase tracking-wide">
            {/* Top Primary Audience split */}
            <div className="mb-4 grid grid-cols-2 gap-2 border-b border-slate-200 pb-4">
              <Link
                to="/private"
                className="flex items-center justify-center gap-2 rounded-sm bg-[#010b27] py-2.5 text-xs text-white font-bold"
              >
                <i className="fa-solid fa-user text-xs" aria-hidden="true" />
                <span>Private</span>
              </Link>
              <Link
                to="/erhvervsdrivende"
                className="flex items-center justify-center gap-2 rounded-sm bg-[#fd7302] py-2.5 text-xs text-white font-bold"
              >
                <i className="fa-solid fa-building text-xs" aria-hidden="true" />
                <span>Erhverv</span>
              </Link>
            </div>

            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `py-2.5 px-3 rounded-sm transition-colors ${
                  isActive ? 'bg-[#fd7302] text-white font-bold' : 'hover:bg-slate-50 text-[#010b27]'
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/private"
              className={({ isActive }) =>
                `py-2.5 px-3 rounded-sm transition-colors ${
                  isActive ? 'bg-[#010b27] text-white font-bold' : 'hover:bg-slate-50 text-[#010b27]'
                }`
              }
            >
              Private
            </NavLink>

            <NavLink
              to="/erhvervsdrivende"
              className={({ isActive }) =>
                `py-2.5 px-3 rounded-sm transition-colors ${
                  isActive ? 'bg-[#fd7302] text-white font-bold' : 'hover:bg-slate-50 text-[#010b27]'
                }`
              }
            >
              Erhvervsdrivende
            </NavLink>

            <div className="py-2">
              <div className="flex items-center justify-between px-3 text-[#010b27] font-bold">
                <Link
                  to="/artikler"
                  onClick={() => setMobileMenuOpen(false)}
                  className="hover:text-[#fd7302]"
                >
                  Artikler
                </Link>
              </div>
              <div className="mt-1 ml-4 pl-2 border-l-2 border-[#fd7302] space-y-1 text-xs normal-case text-slate-600">
                <Link
                  to="/artikler"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    isDropdownActive('/artikler')
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'font-semibold text-[#010b27] hover:text-[#fd7302]'
                  }`}
                >
                  Alle artikler
                </Link>
                <Link
                  to="/artikler?kategori=moms-afgifter"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    isDropdownActive('/artikler', 'kategori=moms-afgifter')
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Moms & Afgifter
                </Link>
                <Link
                  to="/artikler?kategori=indkomstskat"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    isDropdownActive('/artikler', 'kategori=indkomstskat')
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Indkomstskat & Fradrag
                </Link>
                <Link
                  to="/artikler?kategori=selskabsskat"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    isDropdownActive('/artikler', 'kategori=selskabsskat')
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Selskabsskat & Erhverv
                </Link>
                <Link
                  to="/artikler?kategori=skattekontrol"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    isDropdownActive('/artikler', 'kategori=skattekontrol')
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Skattekontrol & Tilsyn
                </Link>
                <Link
                  to="/artikler?kategori=ejendomsskat"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    isDropdownActive('/artikler', 'kategori=ejendomsskat')
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Bolig & Ejendomsskat
                </Link>
              </div>
            </div>

            <NavLink
              to="/presse-og-film"
              className={({ isActive }) =>
                `py-2.5 px-3 rounded-sm transition-colors ${
                  isActive ? 'bg-[#fd7302] text-white font-bold' : 'hover:bg-slate-50 text-[#010b27]'
                }`
              }
            >
              Presse & Film
            </NavLink>

            <div className="py-2">
              <div className="flex items-center justify-between px-3 text-[#010b27] font-bold">
                <Link to="/om-skat" className="hover:text-[#fd7302]">
                  Om skat
                </Link>
              </div>
              <div className="mt-1 ml-4 pl-2 border-l-2 border-[#010b27] space-y-1 text-xs normal-case text-slate-600">
                <Link
                  to="/om-skat/indkomstskat-og-fradragsret"
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    location.pathname === '/om-skat/indkomstskat-og-fradragsret'
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Indkomstskat & Fradragsret
                </Link>
                <Link
                  to="/om-skat/moms-og-afregning-smv"
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    location.pathname === '/om-skat/moms-og-afregning-smv'
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Moms & Momsafregning
                </Link>
                <Link
                  to="/om-skat/skattekontrol-og-tilsyn"
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    location.pathname === '/om-skat/skattekontrol-og-tilsyn'
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Skattekontrol & Tilsyn
                </Link>
                <Link
                  to="/om-skat/boligskat-og-ejendomsvurdering"
                  className={`block py-1.5 px-2 rounded-xs transition-colors ${
                    location.pathname === '/om-skat/boligskat-og-ejendomsvurdering'
                      ? 'bg-[#fff7ed] text-[#fd7302] font-bold border-l-2 border-[#fd7302]'
                      : 'hover:text-[#fd7302]'
                  }`}
                >
                  Boligskat & Grundskyld
                </Link>
              </div>
            </div>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `py-2.5 px-3 rounded-sm transition-colors text-[#fd7302] font-bold ${
                  isActive ? 'bg-[#fd7302] text-white' : 'hover:bg-[#fff7ed]'
                }`
              }
            >
              Har brug for hjælp?
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `py-2.5 px-3 rounded-sm transition-colors ${
                  isActive ? 'bg-[#fd7302] text-white font-bold' : 'hover:bg-slate-50 text-[#010b27]'
                }`
              }
            >
              Kontakt
            </NavLink>

            {/* Direct contact info in mobile menu */}
            <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-600 space-y-2">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-[#fd7302]" aria-hidden="true" />
                <a href="tel:61444604" className="whitespace-nowrap font-medium text-[#010b27] hover:underline">
                  61 44 46 04
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-[#fd7302]" aria-hidden="true" />
                <a href="mailto:mmkh@lexnovo.dk" className="whitespace-nowrap font-medium text-[#010b27] hover:underline">
                  mmkh@lexnovo.dk
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
